import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';

import PORTAL_SELECTORS from '../constants/portalSelectors';

/** Input types a coach types a value into. Checkboxes, radios and the like
 * keep their own Enter behavior. */
const TEXT_INPUT_TYPES = ['', 'text', 'number', 'tel', 'search'];

/**
 * Enter in a plain field of the active exercise collapses it.
 *
 * Before auto-save, the tables were a form with a SAVE button, so Enter in a
 * field submitted it and the item closed. Auto-save removed the form, and
 * Enter stopped doing anything. Collapsing already flushes the pending save
 * (see DraggableContent's collapse-edge effect), so this gives coaches back
 * the same "done with this one" key.
 *
 * Enter belongs to a control first. It is skipped when:
 * - a field already handled it (`defaultPrevented`): the sets stepper commits
 *   its count, and dropdown triggers open or pick an option
 * - the target isn't a text-like input: buttons activate, textareas take a
 *   newline, checkboxes toggle
 * - the input drives a menu (combobox, aria-expanded), a portaled widget or an
 *   open react-select menu has focus, or a modal is up
 *
 * @param {React.RefObject} containerRef ref to the item row.
 * @param {boolean} enabled false opts the item out (not expanded, pre-save,
 *   month popover, or a type that doesn't expand).
 * @param {boolean} isActiveItem whether this row is the active exercise.
 * @param {Function} collapse collapses the item.
 */
const useEnterCollapsesItem = (containerRef, enabled, isActiveItem, collapse) => {
  const store = useStore();
  /** Callers pass a fresh closure each render; read it at keypress time rather
   * than re-subscribing the listener every render. */
  const collapseRef = useRef(collapse);
  collapseRef.current = collapse;

  useEffect(() => {
    if (!enabled || !isActiveItem) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.defaultPrevented) return;
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      /** IME composition commits with Enter. */
      if (e.isComposing || e.keyCode === 229) return;
      if (e.repeat) return;

      const row = containerRef.current;
      const { target } = e;
      if (!row || !row.contains(target)) return;

      if (target.tagName !== 'INPUT') return;
      if (!TEXT_INPUT_TYPES.includes((target.getAttribute('type') || '').toLowerCase())) return;
      if (target.getAttribute('role') === 'combobox') return;
      if (target.hasAttribute('aria-autocomplete')) return;
      if (target.getAttribute('aria-expanded') === 'true') return;
      if (target.closest?.(PORTAL_SELECTORS)) return;
      if (document.querySelector('[class*="__menu"]')) return;

      const { calendar } = store.getState();
      if (calendar.data.activeModal || calendar.data.activeSecondLayerModal) return;
      if (calendar.data.dragIsInProgress) return;

      e.preventDefault();

      /** Blur first, as a click on the collapse icon would: the fields' onBlur
       * handlers normalize their values and queue the save the collapse then
       * flushes. */
      target.blur();
      collapseRef.current();

      /** Keep the keyboard on this row once it closes, so Tab carries on from
       * here instead of the top of the page. */
      requestAnimationFrame(() => {
        const checkbox = containerRef.current?.querySelector('input[type="checkbox"]');
        if (checkbox && checkbox.offsetParent !== null) checkbox.focus({ preventScroll: true });
      });
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [containerRef, enabled, isActiveItem, store]);
};

export default useEnterCollapsesItem;
